import { useEffect, useMemo, useState } from 'react'
import { get } from 'aws-amplify/api'
import { AlertCircle, CalendarClock, Radio, RefreshCw, Server } from 'lucide-react'
import { useAuth } from '@/lib/auth/AuthContext'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import LoadingSpinner from '@/components/LoadingSpinner'
import { useToast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'

const REFRESH_INTERVAL_MS = 60_000

type PlatformKey = 'ios' | 'android' | 'unknown'
type TimingStatus = 'future' | 'current' | 'past'
type InfrastructureStatus = 'scheduled' | 'deploying' | 'deployed' | 'running' | 'complete' | 'missing' | 'error'
type RuleStatus = 'scheduled' | 'disabled' | 'missing' | 'notRequired'

interface VersionBreakdown {
  value: string
  platform: PlatformKey
  count: number
}

interface AudienceSummary {
  totalActiveAudience: number
  uniqueInstallations: number
  platformBreakdown: {
    ios: number
    android: number
    unknown: number
  }
  deliveryModeBreakdown: {
    iosBroadcast: number
    iosLiveActivityToken: number
    androidFcm: number
    unknown: number
  }
  activeIosRegistrations: number
  appVersions: VersionBreakdown[]
  osVersions: VersionBreakdown[]
}

interface EventInfrastructureTask {
  cluster: string
  taskArn?: string
  lastStatus?: string
  desiredStatus?: string
  healthStatus?: string
  startedAt?: string
  group?: string
  serviceName?: string
}

interface EventRuleSnapshot {
  kind:
    | 'deploy'
    | 'pregameWelcome'
    | 'clubroomVisibilityOpen'
    | 'clubroomVisibilityReadOnly'
    | 'clubroomVisibilityClosingSoon'
    | 'clubroomVisibilityHidden'
  ruleName: string
  state: string | null
  scheduleExpression: string | null
}

interface ExpectedRuleStatus {
  ruleName: string
  status: RuleStatus
  state: string | null
  scheduleExpression: string | null
}

interface EventInfrastructureSummary {
  status: InfrastructureStatus
  hasEventState: boolean
  stackName: string
  stackStatus: string | null
  runningTaskCount: number
  tasks: EventInfrastructureTask[]
}

interface EventSchedulesSummary {
  deployRule: ExpectedRuleStatus
  pregameWelcomeRule: ExpectedRuleStatus
  visibilityRules: EventRuleSnapshot[]
  enabledRuleCount: number
  totalRuleCount: number
}

interface TodayEventCard {
  eventId: string
  homeTeam: string
  awayTeam: string
  league: string
  isComplete: boolean
  eventStartTime: string
  timingStatus: TimingStatus
  subscriberMetrics: {
    latestSubscribers: number | null
    latestHourlySubscribers: number | null
    maxSubscribers: number | null
    minSubscribers: number | null
  }
  current: AudienceSummary
  infrastructure: EventInfrastructureSummary
  schedules: EventSchedulesSummary
}

interface EventsOverviewResponse {
  generatedAt: string
  environment: 'prod'
  date: string
  events: TodayEventCard[]
}

const formatEventStartTime = (value: string) =>
  new Date(value).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })

const formatDateTime = (value?: string | null) => {
  if (!value) return '—'
  return new Date(value).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

const formatRuleLabel = (kind: EventRuleSnapshot['kind']) => {
  switch (kind) {
    case 'clubroomVisibilityOpen':
      return 'Clubroom open'
    case 'clubroomVisibilityReadOnly':
      return 'Clubroom read only'
    case 'clubroomVisibilityClosingSoon':
      return 'Clubroom closing soon'
    case 'clubroomVisibilityHidden':
      return 'Clubroom hidden'
    default:
      return kind
  }
}

const formatVersionBreakdowns = (versions: VersionBreakdown[]) => {
  if (!versions.length) return '—'
  return versions
    .slice(0, 3)
    .map((version) => `${version.platform} ${version.value} (${version.count})`)
    .join(', ')
}

const timingBadgeClass = (status: TimingStatus) => {
  switch (status) {
    case 'future':
      return 'bg-sky-500/15 text-sky-100 border-sky-500/20'
    case 'current':
      return 'bg-amber-500/15 text-amber-100 border-amber-500/20'
    case 'past':
      return 'bg-slate-600/40 text-slate-100 border-slate-500/20'
  }
}

const infrastructureBadgeClass = (status: InfrastructureStatus) => {
  switch (status) {
    case 'complete':
      return 'bg-emerald-500/15 text-emerald-100 border-emerald-500/20'
    case 'running':
    case 'deploying':
      return 'bg-amber-500/15 text-amber-100 border-amber-500/20'
    case 'deployed':
    case 'scheduled':
      return 'bg-sky-500/15 text-sky-100 border-sky-500/20'
    case 'error':
      return 'bg-rose-500/15 text-rose-100 border-rose-500/20'
    case 'missing':
      return 'bg-slate-600/40 text-slate-100 border-slate-500/20'
  }
}

const ruleStatusBadgeClass = (status: RuleStatus) => {
  switch (status) {
    case 'scheduled':
      return 'bg-emerald-500/15 text-emerald-100 border-emerald-500/20'
    case 'disabled':
      return 'bg-amber-500/15 text-amber-100 border-amber-500/20'
    case 'missing':
      return 'bg-rose-500/15 text-rose-100 border-rose-500/20'
    case 'notRequired':
      return 'bg-slate-600/40 text-slate-100 border-slate-500/20'
  }
}

export default function EventsPage() {
  const { isAuthenticated, session } = useAuth()
  const { toast } = useToast()
  const [data, setData] = useState<EventsOverviewResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchEvents = async () => {
    if (!session?.tokens?.idToken) {
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      const response = get({
        apiName: 'dashboard',
        path: '/data/events',
        options: {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.tokens.idToken}`,
          },
          withCredentials: true,
        },
      })

      const { body } = await response.response
      const raw = await body.json()
      setData(raw as unknown as EventsOverviewResponse)
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : 'Failed to load events overview'
      setError(message)
      toast({
        title: 'Events overview unavailable',
        description: message,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!isAuthenticated) {
      setData(null)
      return
    }

    fetchEvents()
    const timer = window.setInterval(fetchEvents, REFRESH_INTERVAL_MS)
    return () => window.clearInterval(timer)
  }, [isAuthenticated, session?.tokens?.idToken])

  const summary = useMemo(() => {
    const events = data?.events ?? []
    return {
      eventCount: events.length,
      currentCount: events.filter((event) => event.timingStatus === 'current').length,
      futureCount: events.filter((event) => event.timingStatus === 'future').length,
      totalAudience: events.reduce((total, event) => total + event.current.totalActiveAudience, 0),
      activeTasks: events.reduce((total, event) => total + event.infrastructure.runningTaskCount, 0),
      scheduleIssues: events.reduce((total, event) => {
        const scheduleStatuses = [
          event.schedules.deployRule.status,
          event.schedules.pregameWelcomeRule.status,
          ...event.schedules.visibilityRules.map((rule) =>
            rule.state === 'ENABLED' ? 'scheduled' : rule.state === 'DISABLED' ? 'disabled' : 'missing',
          ),
        ]
        return total + scheduleStatuses.filter((status) => status === 'missing' || status === 'disabled').length
      }, 0),
    }
  }, [data])

  if (isLoading && !data) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <LoadingSpinner size="lg" message="Loading today&apos;s prod events..." />
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <Card className="mx-auto max-w-3xl border-slate-800 bg-slate-900/70 shadow-card">
        <CardHeader className="text-center">
          <CardTitle className="text-3xl font-semibold text-slate-100">ScoreCheck Admin Dashboard</CardTitle>
          <CardDescription className="text-slate-400">
            Sign in to monitor production event readiness and live audience health.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <section className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Events</h1>
          <p className="text-sm text-slate-400">
            Production-only view for today&apos;s events, including audience, infrastructure, and scheduled jobs.
          </p>
        </div>
        <Button variant="outline" className="text-white" onClick={fetchEvents} disabled={isLoading}>
          <RefreshCw className={cn('mr-2 h-4 w-4', isLoading && 'animate-spin')} />
          {isLoading ? 'Refreshing…' : 'Refresh now'}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-sm text-slate-400">
        <Badge className="bg-rose-500/15 text-rose-100 border-rose-500/20">prod</Badge>
        <span>Date: {data?.date ?? '—'}</span>
        <span>Generated: {formatDateTime(data?.generatedAt)}</span>
      </div>

      {error ? (
        <Card className="border-rose-500/40 bg-rose-500/10">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-rose-100">
              <AlertCircle className="h-5 w-5" />
              <p className="font-medium">Unable to load events overview</p>
            </div>
            <p className="mt-1 text-sm text-rose-100/90">{error}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-6">
            <Card className="border-slate-800 bg-slate-900/70">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-300">Today&apos;s events</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-slate-100">{summary.eventCount}</p>
              </CardContent>
            </Card>
            <Card className="border-slate-800 bg-slate-900/70">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-300">Current</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-amber-200">{summary.currentCount}</p>
              </CardContent>
            </Card>
            <Card className="border-slate-800 bg-slate-900/70">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-300">Upcoming</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-sky-200">{summary.futureCount}</p>
              </CardContent>
            </Card>
            <Card className="border-slate-800 bg-slate-900/70">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-300">Active audience</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-slate-100">{summary.totalAudience}</p>
              </CardContent>
            </Card>
            <Card className="border-slate-800 bg-slate-900/70">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-300">Running tasks</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-emerald-200">{summary.activeTasks}</p>
              </CardContent>
            </Card>
            <Card className="border-slate-800 bg-slate-900/70">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-300">Schedule issues</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-rose-200">{summary.scheduleIssues}</p>
              </CardContent>
            </Card>
          </div>

          {data?.events.length ? (
            <div className="grid gap-4 xl:grid-cols-2">
              {data.events.map((event) => (
                <Card key={event.eventId} className="border-slate-800 bg-slate-900/70">
                  <CardHeader className="space-y-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <CardTitle className="text-xl text-slate-100">
                          {event.homeTeam} vs {event.awayTeam}
                        </CardTitle>
                        <CardDescription className="mt-1 text-slate-400">
                          {event.league} · {event.eventId}
                        </CardDescription>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Badge className={timingBadgeClass(event.timingStatus)}>{event.timingStatus}</Badge>
                        <Badge className={infrastructureBadgeClass(event.infrastructure.status)}>
                          {event.infrastructure.status}
                        </Badge>
                        {event.isComplete ? (
                          <Badge className="bg-emerald-500/15 text-emerald-100 border-emerald-500/20">complete</Badge>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-slate-300">
                      <div className="flex items-center gap-2">
                        <CalendarClock className="h-4 w-4 text-slate-500" />
                        <span>{formatEventStartTime(event.eventStartTime)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Radio className="h-4 w-4 text-slate-500" />
                        <span>{event.current.totalActiveAudience} active audience</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Server className="h-4 w-4 text-slate-500" />
                        <span>{event.infrastructure.runningTaskCount} running tasks</span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
                        <p className="text-xs uppercase tracking-wide text-slate-500">Current subscribers</p>
                        <p className="mt-2 text-2xl font-semibold text-slate-100">
                          {event.subscriberMetrics.latestSubscribers ?? '—'}
                        </p>
                      </div>
                      <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
                        <p className="text-xs uppercase tracking-wide text-slate-500">Latest hourly</p>
                        <p className="mt-2 text-2xl font-semibold text-slate-100">
                          {event.subscriberMetrics.latestHourlySubscribers ?? '—'}
                        </p>
                      </div>
                      <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
                        <p className="text-xs uppercase tracking-wide text-slate-500">Peak subscribers</p>
                        <p className="mt-2 text-2xl font-semibold text-slate-100">
                          {event.subscriberMetrics.maxSubscribers ?? '—'}
                        </p>
                      </div>
                      <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
                        <p className="text-xs uppercase tracking-wide text-slate-500">Unique installs</p>
                        <p className="mt-2 text-2xl font-semibold text-slate-100">
                          {event.current.uniqueInstallations}
                        </p>
                      </div>
                    </div>

                    <div className="grid gap-4 xl:grid-cols-2">
                      <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-4">
                        <h2 className="text-sm font-semibold text-slate-200">Subscribers</h2>
                        <div className="mt-3 grid gap-2 text-sm text-slate-300">
                          <div className="flex items-center justify-between">
                            <span>iOS</span>
                            <span>{event.current.platformBreakdown.ios}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>Android</span>
                            <span>{event.current.platformBreakdown.android}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>Broadcast</span>
                            <span>{event.current.deliveryModeBreakdown.iosBroadcast}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>Legacy live activity</span>
                            <span>{event.current.deliveryModeBreakdown.iosLiveActivityToken}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>FCM</span>
                            <span>{event.current.deliveryModeBreakdown.androidFcm}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>Active iOS registrations</span>
                            <span>{event.current.activeIosRegistrations}</span>
                          </div>
                        </div>
                        <div className="mt-4 space-y-2 text-xs text-slate-400">
                          <p>
                            <span className="font-medium text-slate-300">Top app versions:</span>{' '}
                            {formatVersionBreakdowns(event.current.appVersions)}
                          </p>
                          <p>
                            <span className="font-medium text-slate-300">Top OS versions:</span>{' '}
                            {formatVersionBreakdowns(event.current.osVersions)}
                          </p>
                        </div>
                      </div>

                      <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-4">
                        <h2 className="text-sm font-semibold text-slate-200">Infrastructure</h2>
                        <div className="mt-3 grid gap-2 text-sm text-slate-300">
                          <div className="flex items-center justify-between">
                            <span>Event state</span>
                            <span>{event.infrastructure.hasEventState ? 'present' : 'missing'}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>Stack</span>
                            <span>{event.infrastructure.stackStatus ?? '—'}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>Stack name</span>
                            <span className="max-w-[16rem] truncate text-right">{event.infrastructure.stackName}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>Running tasks</span>
                            <span>{event.infrastructure.runningTaskCount}</span>
                          </div>
                        </div>

                        {event.infrastructure.tasks.length ? (
                          <div className="mt-4 space-y-2">
                            {event.infrastructure.tasks.map((task) => (
                              <div
                                key={`${task.cluster}:${task.taskArn ?? task.group ?? 'task'}`}
                                className="rounded-md border border-slate-800 bg-slate-900/80 p-3 text-xs text-slate-300"
                              >
                                <div className="flex items-center justify-between gap-3">
                                  <span className="font-medium text-slate-100">{task.serviceName ?? task.group ?? 'task'}</span>
                                  <span>{task.lastStatus ?? 'unknown'}</span>
                                </div>
                                <div className="mt-1 text-slate-400">
                                  {task.cluster.split('/').pop()} · {formatDateTime(task.startedAt)}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="mt-4 text-xs text-slate-500">No running ECS tasks matched this event.</p>
                        )}
                      </div>
                    </div>

                    <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <h2 className="text-sm font-semibold text-slate-200">EventBridge schedules</h2>
                        <p className="text-xs text-slate-400">
                          {event.schedules.enabledRuleCount} enabled / {event.schedules.totalRuleCount} total
                        </p>
                      </div>
                      <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                        <div className="rounded-md border border-slate-800 bg-slate-900/80 p-3 text-sm">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-slate-200">Deploy</span>
                            <Badge className={ruleStatusBadgeClass(event.schedules.deployRule.status)}>
                              {event.schedules.deployRule.status}
                            </Badge>
                          </div>
                          <p className="mt-2 text-xs text-slate-400">{event.schedules.deployRule.scheduleExpression ?? event.schedules.deployRule.ruleName}</p>
                        </div>
                        <div className="rounded-md border border-slate-800 bg-slate-900/80 p-3 text-sm">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-slate-200">Pregame welcome</span>
                            <Badge className={ruleStatusBadgeClass(event.schedules.pregameWelcomeRule.status)}>
                              {event.schedules.pregameWelcomeRule.status}
                            </Badge>
                          </div>
                          <p className="mt-2 text-xs text-slate-400">
                            {event.schedules.pregameWelcomeRule.scheduleExpression ?? event.schedules.pregameWelcomeRule.ruleName}
                          </p>
                        </div>
                        {event.schedules.visibilityRules.map((rule) => (
                          <div key={rule.ruleName} className="rounded-md border border-slate-800 bg-slate-900/80 p-3 text-sm">
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-slate-200">{formatRuleLabel(rule.kind)}</span>
                              <Badge className={ruleStatusBadgeClass(rule.state === 'ENABLED' ? 'scheduled' : rule.state === 'DISABLED' ? 'disabled' : 'missing')}>
                                {rule.state?.toLowerCase() ?? 'missing'}
                              </Badge>
                            </div>
                            <p className="mt-2 text-xs text-slate-400">{rule.scheduleExpression ?? rule.ruleName}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-slate-800 bg-slate-900/70">
              <CardContent className="pt-6">
                <p className="text-sm text-slate-400">No prod events are scheduled for today.</p>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </section>
  )
}
